import { useCallback, useMemo, useRef, useState } from 'react';

import { s } from './lib/style.js';
import { useIsNarrow } from './lib/useIsNarrow.js';
import { useNavAutoHide } from './lib/useNavAutoHide.js';
import { useScrollReveal } from './lib/useScrollReveal.js';
import { connectors, ecosystemRows } from './data/integrations.js';
import { submitInquiry } from './lib/submitInquiry.js';
import {
  Ecosystem,
  Faq,
  Footer,
  Foundation,
  Hero,
  Nav,
  Partnership,
  Platform,
  Problem,
  Security,
  Team,
  ToolMarquee,
  Waitlist,
} from './sections/index.js';

const ACCENT = '#2E7A72';
const CTA_LABEL = 'Become a design partner';

const ROOT_STYLE =
  '--ink:#FFFFFF;--panel:#F4F8F7;--panel-2:#E8F0EE;--line:rgba(18,49,56,0.11);--text:#123138;' +
  "--muted:#5C6C6E;--mono:'DM Mono',monospace;--mono-w:500;--mint:#2E7A72;--teal:#5B9A96;" +
  '--paper:#EDF3F2;--navy:#123138;--paper-muted:#5C6C6E;--paper-line:rgba(18,49,56,0.12);' +
  "font-family:'Sofia Pro',sans-serif;background:var(--ink);color:var(--text);" +
  '-webkit-font-smoothing:antialiased;overflow-x:clip;';

// Headline accent treatment. The export shipped a runtime switcher between a
// flat colour and a gradient; the gradient is the shipped design.
const HEADING_GRADIENT = true;

// Layout of the Solution section: the stacked treatment is the shipped design,
// the alternative being a ledger-style split.
const SOLUTION_STACKED = true;

const BURGER_BAR =
  'display:block;width:24px;height:2px;border-radius:2px;background:var(--text);' +
  'transition:transform .28s ease,opacity .2s ease;';

const HEADING_ON_LIGHT = HEADING_GRADIENT
  ? 'background:linear-gradient(180deg,#7ACEC0 0%,#5B9A96 30%,#2E7A72 66%,#123138 100%);' +
    '-webkit-background-clip:text;background-clip:text;color:transparent;-webkit-text-fill-color:transparent;'
  : 'color:var(--mint);';

const HEADING_ON_DARK = HEADING_GRADIENT
  ? 'background:linear-gradient(180deg,#D9F2EC 0%,#7ACEC0 44%,#5B9A96 100%);' +
    '-webkit-background-clip:text;background-clip:text;color:transparent;-webkit-text-fill-color:transparent;'
  : 'color:#7ACEC0;';

const STEP_NUMBER =
  "font-family:'Sofia Pro',sans-serif;font-weight:700;font-size:clamp(48px,4.6vw,64px);" +
  'line-height:0.9;letter-spacing:-0.04em;' +
  (HEADING_GRADIENT
    ? 'background:linear-gradient(180deg,rgba(255,255,255,0.85) 0%,rgba(217,242,236,0.55) 46%,rgba(122,206,192,0.28) 100%);' +
      '-webkit-background-clip:text;background-clip:text;color:transparent;-webkit-text-fill-color:transparent;'
    : 'color:rgba(255,255,255,0.30);');

const SOLUTION_SECTION =
  'scroll-margin-top:80px;position:relative;overflow:hidden;' +
  (SOLUTION_STACKED
    ? 'background:var(--paper);color:var(--navy);'
    : 'background:#0B1F23;color:#EAF3F1;');

const EMPTY_FORM = { name: '', company: '', email: '', message: '' };

export default function App() {
  const rootRef = useRef(null);
  const navRef = useRef(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(1);
  const [openBio, setOpenBio] = useState(0);
  const [navOpen, setNavOpen] = useState(false);
  const [mockNavOpen, setMockNavOpen] = useState(false);
  const isNarrow = useIsNarrow();

  useNavAutoHide(navRef);
  useScrollReveal(rootRef);

  const field = useCallback(
    (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value })),
    [],
  );

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!form.email.includes('@')) return;
      setSubmitted(true);
      submitInquiry(form);
    },
    [form],
  );

  // The generated sections read every dynamic value off a single object, which
  // is how the original template bound them. Styles stay as CSS strings so the
  // markup keeps matching the design export line for line.
  const v = useMemo(() => {
    const values = {
      ctaLabel: CTA_LABEL,

      navRow: isNarrow
        ? 'display:none;'
        : 'display:flex;flex-wrap:wrap;align-items:center;justify-content:flex-end;gap:16px 24px;',
      navDrawer:
        isNarrow && navOpen ? 'display:block;border-top:1px solid var(--line);' : 'display:none;',
      toggleNav: () => setNavOpen((open) => !open),
      closeNav: () => setNavOpen(false),

      burgerBtn:
        'appearance:none;background:none;border:none;cursor:pointer;padding:10px;margin:-10px;' +
        (isNarrow ? 'display:flex;' : 'display:none;') +
        'flex-direction:column;gap:5px;',
      burgerTop: BURGER_BAR + (navOpen ? 'transform:translateY(7px) rotate(45deg);' : ''),
      burgerMid: BURGER_BAR + (navOpen ? 'opacity:0;' : ''),
      burgerBot: BURGER_BAR + (navOpen ? 'transform:translateY(-7px) rotate(-45deg);' : ''),

      // Chrome of the in-page product mock-up.
      mockGrid: isNarrow
        ? 'grid-template-columns:minmax(0,1fr);'
        : 'grid-template-columns:minmax(0,212px) minmax(0,1fr);',
      mockSidePanel:
        'border-right:1px solid rgba(255,255,255,0.10);padding:18px 14px;flex-direction:column;' +
        'gap:18px;background:#0B1F23;' +
        (isNarrow && !mockNavOpen ? 'display:none;' : 'display:flex;'),
      mockToggle:
        'appearance:none;background:none;border:none;cursor:pointer;padding:8px;' +
        'margin:-8px 0 -8px auto;flex-direction:column;gap:4px;' +
        (isNarrow ? 'display:flex;' : 'display:none;'),
      toggleMockNav: () => setMockNavOpen((open) => !open),

      kwLight: HEADING_ON_LIGHT,
      kwDark: HEADING_ON_DARK,
      kwNum: STEP_NUMBER,
      solSection: SOLUTION_SECTION,
      solLayoutA: SOLUTION_STACKED,
      solLayoutB: !SOLUTION_STACKED,

      ecoRowA: ecosystemRows.data,
      ecoRowB: ecosystemRows.marketing,
      ecoRowC: ecosystemRows.workflow,
      connectors,

      ...form,
      submitted,
      notSubmitted: !submitted,
      onName: field('name'),
      onCompany: field('company'),
      onEmail: field('email'),
      onMessage: field('message'),
      onSubmit,

      teamOff: () => setOpenBio(0),
    };

    for (const n of [1, 2, 3, 4]) {
      const open = openBio === n;
      values[`bioMax${n}`] = open ? '240px' : '0px';
      values[`bioOp${n}`] = open ? 1 : 0;
      values[`cueLabel${n}`] = open ? 'Less' : 'More';
      values[`cueH${n}`] = open ? '0px' : '20px';
      values[`cuePad${n}`] = open ? '0px' : '14px';
      values[`cueOp${n}`] = open ? 0 : 0.45;
      values[`cueRot${n}`] = open ? 'rotate(180deg)' : 'rotate(0deg)';
      values[`teamOn${n}`] = () => setOpenBio(n);
    }

    for (const n of [1, 2, 3, 4, 5, 6]) {
      values[`open${n}`] = openFaq === n;
      values[`sign${n}`] = openFaq === n ? '−' : '+';
      values[`toggle${n}`] = () => setOpenFaq((current) => (current === n ? 0 : n));
    }

    return values;
  }, [field, form, isNarrow, mockNavOpen, navOpen, onSubmit, openBio, openFaq, submitted]);

  return (
    <div id="bb" ref={rootRef} style={{ ...s(ROOT_STYLE), '--mint': ACCENT }}>
      <Nav v={v} navRef={navRef} />
      <Hero v={v} />
      <ToolMarquee v={v} />
      <Problem />
      <Platform v={v} />
      <Ecosystem v={v} />
      <Foundation v={v} />
      <Security v={v} />
      <Team v={v} />
      <Partnership v={v} />
      <Faq v={v} />
      <Waitlist v={v} />
      <Footer />
    </div>
  );
}
